import CategoriasDoGrupo, { type Categoria } from './CategoriasDoGrupo';
import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { RotinaGrupo, RotinaGrupoSalvo } from '@/services/api/rotinas';
import { hhmm, horaExibida, tituloDoGrupo, traducaoDoGrupo } from './rotinaUtils';
import { ResultadoDaEdicao } from './ResultadosDoGrupo';

const COLUNAS = 'grid-cols-[84px_minmax(0,1fr)_170px_56px_84px]';

/**
 * O grupo dispara às `hora_local` de cada página, então em Brasília ele não
 * tem UMA hora: com páginas em três fusos são três horários. A coluna mostra o
 * primeiro e marca com "+"; o detalhe fica aqui.
 */
function horaBrtTitulo(g: RotinaGrupo): string {
    if (!g.hora_brt) return 'Sem página no grupo: não há como calcular o horário de Brasília.';
    const faixa = g.hora_brt_fim && g.hora_brt_fim !== g.hora_brt
        ? `Primeiro disparo ${g.hora_brt}, último ${g.hora_brt_fim} (horário de Brasília) — as páginas estão em fusos diferentes.`
        : `${g.hora_brt} no horário de Brasília.`;
    return `${faixa} No relógio de cada página são sempre ${hhmm(g.hora_local)}.`;
}

function CelulaTraducao({ grupo }: { grupo: RotinaGrupo }) {
    const t = traducaoDoGrupo(grupo);
    if (t.tipo === 'idiomas') {
        return (
            <span
                className="inline-flex max-w-full items-center truncate rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                title={`Idiomas guardados: ${t.idiomas.join(', ')}`}
            >
                {t.idiomas.length} idioma{t.idiomas.length > 1 ? 's' : ''}
            </span>
        );
    }
    if (t.tipo === 'utility') {
        return (
            <span
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                title="O texto do template de utilidade já foi aprovado por página — não há tradução a fazer."
            >
                Texto do template
            </span>
        );
    }
    if (t.tipo === 'ia') {
        return (
            <span
                className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800"
                // "Auto" é uma escolha válida, não configuração faltando: sem idioma de
                // origem o prompt manda a IA detectar sozinha ("Auto-detect the source
                // language"). O texto anterior parecia denunciar um campo em branco.
                title={`Traduz por IA na materialização${t.origem ? `, a partir de ${t.origem}` : ', detectando o idioma de origem automaticamente'}`}
            >
                Traduz por IA
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900"
            title="Nenhum idioma guardado e sem tradução por IA: só o texto do idioma-fonte vai sair."
        >
            Sem tradução
        </span>
    );
}

function Linha({
    grupo, diaSemana, tituloComum, maxPaginas, categorias, ocupado, resultado,
    onEditar, onApagar, onLigar, onDispensar, somenteLeitura,
}: {
    grupo: RotinaGrupo;
    diaSemana: number;
    tituloComum: boolean;
    maxPaginas: number;
    categorias: Categoria[];
    ocupado: boolean;
    resultado?: RotinaGrupoSalvo;
    onEditar: () => void;
    onApagar: () => void;
    onLigar: (ativa: boolean) => void;
    onDispensar: () => void;
    somenteLeitura?: boolean;
}) {
    const total = grupo.paginas.length;
    const largura = maxPaginas > 0 ? Math.max(2, Math.round((total / maxPaginas) * 100)) : 0;
    return (
        <div className="border-t">
            <div className={`grid ${COLUNAS} items-center gap-3 px-4 py-1.5 ${grupo.ativa ? '' : 'opacity-60'}`}>
                {/* A hora lidera porque é a única coisa que distingue um grupo do outro:
                    numa Rotina real os dez usam o mesmo título.

                    Brasília em cima, para bater com o calendário e a tela de
                    broadcast. A hora local fica embaixo porque é ela que o
                    molde guarda e que o editor abre — esconder faria o número
                    mudar ao clicar em editar. */}
                <span className="flex flex-col leading-tight">
                    <span className="text-base font-semibold tabular-nums" title={horaBrtTitulo(grupo)}>
                        {horaExibida(grupo)}
                        {grupo.hora_brt_fim && grupo.hora_brt_fim !== grupo.hora_brt && (
                            <span className="text-muted-foreground">+</span>
                        )}
                    </span>
                    {/* Só quando há o que comparar: sem `hora_brt` as duas linhas
                        mostrariam o mesmo número, e a legenda "local" mentiria
                        sobre qual régua está sendo usada. */}
                    {grupo.hora_brt && (
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                            {hhmm(grupo.hora_local)} local
                        </span>
                    )}
                </span>

                <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                        <span className="min-w-[2.5rem] text-sm font-medium tabular-nums" title={`${total} páginas, as mesmas que o seletor mostra.`}>
                            {total.toLocaleString('pt-BR')}
                        </span>
                        <span className="h-1.5 max-w-[180px] flex-1 overflow-hidden rounded-full bg-muted">
                            <span className="block h-1.5 rounded-full bg-muted-foreground/60" style={{ width: `${largura}%` }} />
                        </span>
                        <CategoriasDoGrupo paginas={grupo.paginas} categorias={categorias} />
                    </div>
                    {!tituloComum && (
                        <div className="truncate text-xs text-muted-foreground" title={grupo.titulo_modelo}>
                            {tituloDoGrupo(grupo, diaSemana)}
                        </div>
                    )}
                    {!grupo.ativa && (
                        <Badge variant="secondary" className="mt-0.5">desligado</Badge>
                    )}
                    {!!grupo.utility_esteira_id && (
                        <Badge
                            variant="secondary"
                            className="mt-0.5 ml-1"
                            title="As páginas vêm da Esteira, resolvidas a cada materialização — por isso a contagem acima pode estar zerada."
                        >
                            esteira
                        </Badge>
                    )}
                    {!!grupo.utility_template_id && (
                        <Badge variant="secondary" className="mt-0.5 ml-1" title="Grupo de template utility: sai fora da janela de 24h.">
                            utility
                        </Badge>
                    )}
                    {!!grupo.message_tag && (
                        <Badge variant="secondary" className="mt-0.5 ml-1" title={`Enviado com a tag ${grupo.message_tag}.`}>
                            {grupo.message_tag}
                        </Badge>
                    )}
                </div>

                <span className="min-w-0"><CelulaTraducao grupo={grupo} /></span>

                <Switch
                    checked={!!grupo.ativa}
                    disabled={ocupado || somenteLeitura}
                    onCheckedChange={onLigar}
                    aria-label={`Grupo das ${horaExibida(grupo)} ligado`}
                />

                <span className="flex justify-end">
                    {!somenteLeitura && (<>
                    <Button variant="ghost" size="sm" disabled={ocupado} onClick={onEditar} aria-label={`Editar grupo das ${horaExibida(grupo)}`}>
                        <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" disabled={ocupado} onClick={onApagar} aria-label={`Apagar grupo das ${horaExibida(grupo)}`}>
                        <Trash2 className="h-4 w-4" />
                    </Button>
                    </>)}
                </span>
            </div>
            {/* O resultado da edição ocupa a largura toda: são seis contadores e dois avisos. */}
            {resultado && (
                <div className="border-t bg-muted/20 px-4 py-2">
                    <ResultadoDaEdicao r={resultado} onFechar={onDispensar} />
                </div>
            )}
        </div>
    );
}

export default function GruposTabela({
    grupos, diaSemana, tituloComum, categorias, ocupado, salvos,
    onEditar, onApagar, onLigar, onDispensar, somenteLeitura,
}: {
    grupos: RotinaGrupo[];
    diaSemana: number;
    tituloComum: boolean;
    categorias: Categoria[];
    ocupado: boolean;
    salvos: Record<number, RotinaGrupoSalvo>;
    onEditar: (g: RotinaGrupo) => void;
    onApagar: (g: RotinaGrupo) => void;
    onLigar: (g: RotinaGrupo, ativa: boolean) => void;
    onDispensar: (g: RotinaGrupo) => void;
    somenteLeitura?: boolean;
}) {
    const maxPaginas = grupos.reduce((m, g) => Math.max(m, g.paginas.length), 0);
    return (
        <div className="border-t">
            <div className={`grid ${COLUNAS} gap-3 bg-muted/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground`}>
                <span title="Horário de Brasília, como no calendário e na tela de broadcasts.">
                    Hora <span className="normal-case">(Brasília)</span>
                </span>
                <span>Páginas</span><span>Tradução</span><span>Ativo</span>
                <span className="text-right">Ações</span>
            </div>
            {grupos.map((g) => (
                <Linha
                    key={g.id}
                    grupo={g}
                    diaSemana={diaSemana}
                    tituloComum={tituloComum}
                    maxPaginas={maxPaginas}
                    categorias={categorias}
                    ocupado={ocupado}
                    resultado={salvos[g.id]}
                    onEditar={() => onEditar(g)}
                    onApagar={() => onApagar(g)}
                    onLigar={(ativa) => onLigar(g, ativa)}
                    onDispensar={() => onDispensar(g)}
                    somenteLeitura={somenteLeitura}
                />
            ))}
        </div>
    );
}
